'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams, useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { createClient } from '@/lib/supabase/browser';
import { Button } from '@/components/storefront/ui/button';
import { Field, Input } from '@/components/storefront/ui/input';
import { useState } from 'react';
import { Eye, EyeSlash, EnvelopeSimple, LockSimple, SpinnerGap } from '@phosphor-icons/react';
import { Link } from '@/i18n/navigation';
import { OAuthButtons } from './OAuthButtons';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

type LoginValues = z.infer<typeof loginSchema>;

export function LoginForm() {
  const t = useTranslations('auth');
  const router = useRouter();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const resetSuccess = searchParams.get('reset') === 'success';
  const next = searchParams.get('next');

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginValues) => {
    setServerError(null);
    setNeedsVerification(false);
    setResendDone(false);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });

    if (error) {
      if (error.message.toLowerCase().includes('email not confirmed')) {
        setNeedsVerification(true);
      } else {
        setServerError(t('incorrectCredentials'));
      }
      return;
    }

    // Join this store. One identity can be a member of many stores, each with
    // isolated data — so an email registered on another tenant logs in fine and
    // simply gains a membership here. The server adds it (the active tenant comes
    // from the domain, not the client); if it was newly added we refresh the
    // session so the JWT carries the updated app_metadata.tenants claim.
    try {
      const res = await fetch('/api/auth/join-tenant', { method: 'POST' });
      if (res.ok) {
        const { added } = (await res.json()) as { added?: boolean };
        if (added) await supabase.auth.refreshSession();
      }
    } catch {
      // Non-fatal: existing members are already provisioned; a transient join
      // failure shouldn't block login. RLS still scopes them to this tenant.
    }

    router.push(next ?? `/${locale}`);
    router.refresh();
  };

  const handleResendVerification = async () => {
    const supabase = createClient();
    const email = getValues('email');
    await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback?locale=${locale}` },
    });
    setResendDone(true);
  };

  return (
    <div className="w-full max-w-md space-y-6">
      {resetSuccess && (
        <div className="bg-success/10 border border-success/20 text-success rounded-xl px-4 py-3 text-sm">
          {t('passwordResetSuccess')}
        </div>
      )}

      <OAuthButtons next={next} />

      <div className="relative flex items-center">
        <div className="flex-1 border-t border-border" />
        <span className="px-3 text-xs text-muted-foreground uppercase tracking-wider">
          {t('orContinueWith')}
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <Field label={t('email')} error={errors.email?.message}>
          {(id, describedBy) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              size="lg"
              prefix={<EnvelopeSimple size={18} />}
              invalid={!!errors.email}
              aria-describedby={describedBy}
              placeholder="you@example.com"
              {...register('email')}
            />
          )}
        </Field>

        <Field label={t('password')} error={errors.password?.message}>
          {(id, describedBy) => (
            <Input
              id={id}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              size="lg"
              prefix={<LockSimple size={18} />}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="pointer-events-auto p-1 -m-1 hover:text-foreground transition-colors"
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              }
              invalid={!!errors.password}
              aria-describedby={describedBy}
              {...register('password')}
            />
          )}
        </Field>

        <div className="flex justify-end -mt-2">
          <Link href="/forgot-password" className="text-sm text-primary hover:underline">
            {t('forgotPassword')}
          </Link>
        </div>

        {serverError && (
          <p className="text-sm text-danger" role="alert">{serverError}</p>
        )}

        {needsVerification && (
          <div className="space-y-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3">
            <p className="text-sm text-foreground">{t('pleaseVerify')}</p>
            {resendDone ? (
              <p className="text-sm text-muted-foreground">{t('checkEmailNewLink')}</p>
            ) : (
              <Button type="button" variant="text" color="primary" size="sm" onClick={handleResendVerification}>
                {t('resendVerification')}
              </Button>
            )}
          </div>
        )}

        <Button
          type="submit"
          variant="solid"
          color="primary"
          size="lg"
          fullWidth
          disabled={isSubmitting}
          leftIcon={isSubmitting ? <SpinnerGap className="animate-spin" /> : null}
        >
          {t('login')}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        {t('noAccount')}{' '}
        <Link href="/signup" className="text-primary hover:underline font-medium">
          {t('signup')}
        </Link>
      </p>
    </div>
  );
}
